import { ConnectionPointType } from "../connection/connectionPointType.js";
import { ConnectionPoint } from "../connection/connectionPoint.js";
import { ConnectionPointWithDefault } from "../connection/connectionPointWithDefault.js";
import { ConnectionPointDirection } from "../connection/connectionPointDirection.js";
import { UniqueIdGenerator } from "../utils/uniqueIdGenerator.js";
/**
 * This class represents the base class for all smart filter blocks.
 *
 * It defines the basic structure of a smart filter block and provides the base implementation for
 * managing the connection points.
 *
 * It enforces common behavior for all smart filter blocks.
 */
export class BaseBlock {
    /**
     * The type of the block - used when serializing / deserializing the block, and in the editor.
     * For programmatically created blocks, this should be the class name of the block.
     * For custom blocks, this is specified in the block definition.
     */
    get blockType() {
        return this.getClassName();
    }
    /**
     * The namespace of the block, which is used to reduce name collisions between blocks and also to group blocks in the editor UI.
     * By convention, sub namespaces are separated by a period (e.g. "Babylon.Demo.Effects").
     */
    get namespace() {
        // Note that we use a static property instead of doing this.constructor.name to avoid problems with minifiers that would change the name of the class
        return this.constructor.Namespace;
    }
    /**
     * Instantiates a new block.
     * @param smartFilter - Defines the smart filter the block belongs to
     * @param name - Defines the name of the block
     * @param disableOptimization - Defines if the block is optimizable or not
     */
    constructor(smartFilter, name, disableOptimization = false) {
        this.disableOptimization = disableOptimization;
        /**
         * User provided comments about the block. It can be used to document the block.
         */
        this.comments = null;
        this._inputs = [];
        this._outputs = [];
        this.uniqueId = UniqueIdGenerator.UniqueId;
        this.name = name;
        this.smartFilter = smartFilter;
        // Register the block in the smart filter
        smartFilter.registerBlock(this);
    }
    /**
     * Returns the inputs connection points of the current block.
     */
    get inputs() {
        return this._inputs;
    }
    /**
     * Returns the outputs connection points of the current block.
     */
    get outputs() {
        return this._outputs;
    }
    /**
     * Returns if the block is an input block.
     */
    get isInput() {
        return this._inputs.length === 0;
    }
    /**
     * Returns if the block is an output block.
     */
    get isOutput() {
        return this._outputs.length === 0;
    }
    /**
     * @returns the class name of the block
     */
    getClassName() {
        // Note that we use a static property instead of doing this.constructor.name to avoid problems with minifiers that would change the name of the class
        return this.constructor.ClassName;
    }
    /**
     * Checks if the block is an "ancestor" of another giving block.
     * @param block - Defines the block to check against
     * @returns True if the block is an ancestor of the given block, otherwise false
     */
    isAnAncestorOf(block) {
        for (const output of this._outputs) {
            if (!output.endpoints.length) {
                continue;
            }
            for (const endpoint of output.endpoints) {
                if (endpoint.ownerBlock === block) {
                    return true;
                }
                if (endpoint.ownerBlock.isAnAncestorOf(block)) {
                    return true;
                }
            }
        }
        return false;
    }
    _visitInputs(extraData, callback, alreadyVisited) {
        for (const input of this.inputs) {
            if (!input.connectedTo) {
                continue;
            }
            const block = input.connectedTo.ownerBlock;
            block.visit(extraData, callback, alreadyVisited);
        }
    }
    /**
     * Visits the block and its inputs recursively.
     * When starting from the smart filter output block, this will visit all the blocks in the smart filter.
     * Note that it's a depth first visit: the callback is called on the block AFTER visiting its inputs.
     * @param extraData - The extra data to pass to the callback
     * @param callback - The callback to call on each block
     * @param alreadyVisitedBlocks  - Defines the set of blocks already visited (if not provided, a new set will be created)
     */
    visit(extraData, callback, alreadyVisitedBlocks) {
        if (!alreadyVisitedBlocks) {
            alreadyVisitedBlocks = BaseBlock._AlreadyVisitedBlocks;
            alreadyVisitedBlocks.clear();
        }
        if (!alreadyVisitedBlocks.has(this)) {
            alreadyVisitedBlocks.add(this);
            this._visitInputs(extraData, callback, alreadyVisitedBlocks);
            callback(this, extraData);
        }
    }
    /**
     * Finds the input connection point with the given name.
     * @param name - Name of the input to find
     * @returns The connection point with the given name or null if not found
     */
    findInput(name) {
        for (const input of this._inputs) {
            if (input.name === name) {
                return input;
            }
        }
        return null;
    }
    /**
     * Disconnects the block from the graph.
     * @param _disconnectedConnections - Stores the connections that have been broken in the process. You can reconnect them later if needed.
     */
    disconnectFromGraph(_disconnectedConnections) { }
    /**
     * Prepares the block for runtime.
     * This is called by the smart filter just before creating the smart filter runtime, and by the optimizer.
     */
    prepareForRuntime() { }
    /**
     * Propagates the runtime data - telling all outputs to propagate their runtime data forward through the graph
     */
    propagateRuntimeData() {
        for (const output of this._outputs) {
            output.propagateRuntimeData();
        }
    }
    /**
     * Generates the commands needed to execute the block at runtime and gathers promises for initialization work
     * @param initializationData - The initialization data to use
     * @param _finalOutput - Defines if the block is the final output of the smart filter
     */
    generateCommandsAndGatherInitPromises(initializationData, _finalOutput) {
        // Check if any inputs are Textures which aren't yet ready, and if so, ensure init waits for them to be ready
        for (const input of this._inputs) {
            if (input.type === ConnectionPointType.Texture) {
                const texture = input.runtimeData?.value;
                if (texture && !texture.isReady()) {
                    const internalTexture = texture.getInternalTexture();
                    if (internalTexture) {
                        const textureReadyPromise = new Promise((resolve, reject) => {
                            internalTexture.onLoadedObservable.add(() => {
                                resolve();
                            });
                            internalTexture.onErrorObservable.add((error) => {
                                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                                reject(error);
                            });
                        });
                        initializationData.initializationPromises.push(textureReadyPromise);
                    }
                }
            }
        }
    }
    /**
     * Disconnects all the inputs and outputs from the Block.
     */
    disconnect() {
        // Detach inputs
        for (const input of this._inputs) {
            input.connectedTo?.disconnectFrom(input);
        }
        // Detach outputs
        for (const output of this._outputs) {
            output.disconnectAllEndpoints();
        }
    }
    /**
     * Registers a new input connection point in the block which must have a connection before the graph can be used.
     * @param name - Defines the name of the input connection point
     * @param type - Defines the type of the input connection point
     * @param defaultValue - Defines the optional default value of the input connection point to use if not connection is made
     * @returns The new ConnectionPoint
     * @internal
     */
    _registerInput(name, type, defaultValue = null) {
        const input = new ConnectionPoint(name, this, type, ConnectionPointDirection.Input, defaultValue);
        this._inputs.push(input);
        return input;
    }
    /**
     * Registers a new input connection point in the block which doesn't require a connection because it has a default value.
     * @param name - Defines the name of the input connection point
     * @param type - Defines the type of the input connection point
     * @param defaultValue - Defines the default value to use if nothing is connected to this connection point
     * @returns The new ConnectionPointWithDefault
     * @internal
     */
    _registerOptionalInput(name, type, defaultValue) {
        const input = new ConnectionPointWithDefault(name, this, type, ConnectionPointDirection.Input, defaultValue);
        this._inputs.push(input);
        return input;
    }
    /**
     * Registers a new output connection point in the block.
     * @param name - Defines the name of the output connection point
     * @param type - Defines the type of the output connection point
     * @returns The new output connection point
     * @internal
     */
    _registerOutput(name, type) {
        const output = new ConnectionPoint(name, this, type, ConnectionPointDirection.Output);
        this._outputs.push(output);
        return output;
    }
    /**
     * Registers a new output connection point in the block that always has runtimeData because it has a default value and doesn't allow it to be overwritten with null.
     * @param name - Defines the name of the output connection point
     * @param type - Defines the type of the output connection point
     * @param initialValue - Defines the initial value of the output connection point
     * @returns The new output connection point with a default value
     * @internal
     */
    _registerOutputWithDefault(name, type, initialValue) {
        const output = new ConnectionPointWithDefault(name, this, type, ConnectionPointDirection.Output, initialValue);
        this._outputs.push(output);
        return output;
    }
    /**
     * Gets the required RuntimeData for the given input, throwing with a clear message if it is null
     * @param input - The input to get the runtime data for
     * @returns The runtimeData or throws if it was undefined
     */
    _confirmRuntimeDataSupplied(input) {
        if (!input.runtimeData) {
            throw new Error(`The ${ConnectionPointType[input.type]} input named "${input.name}" is missing for the ${this.getClassName()} named "${this.name}"`);
        }
        return input.runtimeData;
    }
}
BaseBlock._AlreadyVisitedBlocks = new Set();
/**
 * The class name of the block.
 */
BaseBlock.ClassName = "BaseBlock";
/**
 * The namespace of the block, which is used to reduce name collisions between blocks and also to group blocks in the editor UI.
 * By convention, sub namespaces are separated by a period (e.g. "Babylon.Demo.Effects").
 */
BaseBlock.Namespace = null;
//# sourceMappingURL=baseBlock.js.map